/**
 * Single source of truth for how the app DISPLAYS a time-of-day to a human.
 *
 * Storage, validation, and every API payload stay 24-hour (`HH:MM`) —
 * unrelated to this file. This module only controls rendering: every
 * component that shows a lecture/booking/availability time (or configures a
 * FullCalendar instance) should go through the functions below instead of
 * calling `toLocaleTimeString()`/`Intl.DateTimeFormat()` directly, so the
 * whole app's displayed format can be changed from the one setting below.
 */

/** Change this to '24h' (or set NEXT_PUBLIC_TIME_FORMAT=24h) to switch every time display in the app. */
const TIME_DISPLAY_FORMAT: "12h" | "24h" =
  process.env.NEXT_PUBLIC_TIME_FORMAT === "24h" ? "24h" : "12h";

export function isTwelveHourTimeFormat(): boolean {
  return TIME_DISPLAY_FORMAT === "12h";
}

export function resolveDateTimeLocale(locale: string): string {
  return locale === "ar" ? "ar-EG" : "en-US";
}

function timeOptions(extra?: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
  return {
    hour: "2-digit",
    minute: "2-digit",
    hour12: isTwelveHourTimeFormat(),
    ...extra,
  };
}

function formatWithOptions(
  date: Date,
  locale: string,
  options: Intl.DateTimeFormatOptions,
  timeZone?: string
): string {
  try {
    return new Intl.DateTimeFormat(resolveDateTimeLocale(locale), {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(resolveDateTimeLocale(locale), options).format(date);
  }
}

/** Date-only, in a specific IANA timezone (e.g. the lecture's student/teacher timezone). Includes the weekday name (e.g. "Sun, Jul 16, 2026"). */
export function formatLectureDateInTimezone(date: Date, locale: string, timeZone: string): string {
  return formatWithOptions(
    date,
    locale,
    { weekday: "short", year: "numeric", month: "short", day: "2-digit" },
    timeZone
  );
}

/** Time-only, in a specific IANA timezone. Follows the app-wide 12h/24h setting. */
export function formatLectureTimeInTimezone(
  date: Date,
  locale: string,
  timeZone: string,
  opts?: { seconds?: boolean }
): string {
  return formatWithOptions(date, locale, timeOptions(opts?.seconds ? { second: "2-digit" } : undefined), timeZone);
}

/** Date + time together, in a specific IANA timezone. Follows the app-wide 12h/24h setting. Pass `weekday: true` to prefix the weekday name (e.g. "Sun, 07/16/2026, ..."). */
export function formatLectureDateTimeInTimezone(
  date: Date,
  locale: string,
  timeZone: string,
  opts?: { weekday?: boolean }
): string {
  return formatWithOptions(
    date,
    locale,
    {
      ...(opts?.weekday ? { weekday: "short" as const } : {}),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      ...timeOptions(),
    },
    timeZone
  );
}

/**
 * Time-only, using the browser's local timezone (no explicit IANA zone) —
 * for call sites that historically rendered `new Date(utc)` without
 * converting to a specific lecture timezone. Follows the app-wide setting.
 */
export function formatLocalTime(date: Date | string, locale: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return formatWithOptions(d, locale, timeOptions());
}

/**
 * Formats a raw, timezone-less "HH:MM" (or "HH:MM:SS") string — e.g. a
 * booking slot label that's already resolved to the right local time and
 * only needs its 12h/24h display decided. Follows the app-wide setting.
 */
export function formatRawTimeString(time: string, locale: string): string {
  const [hours, minutes] = time.split(":").map(Number);

  if (!time || Number.isNaN(hours) || Number.isNaN(minutes)) {
    return time ?? "";
  }

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return formatWithOptions(date, locale, timeOptions());
}

/**
 * FullCalendar's `eventTimeFormat`/`slotLabelFormat` props take a format
 * object (Intl-like, plus FullCalendar's own `meridiem` key) rather than a
 * render function — this is the one place that object is built, so every
 * FullCalendar instance in the app renders times the same way.
 */
export function getFullCalendarTimeFormat(): {
  hour: "numeric";
  minute: "2-digit";
  hour12: boolean;
  meridiem?: "short";
} {
  const hour12 = isTwelveHourTimeFormat();

  return {
    hour: "numeric",
    minute: "2-digit",
    hour12,
    ...(hour12 ? { meridiem: "short" as const } : {}),
  };
}

/**
 * Returns the wall-clock time of `date` in the given IANA timezone as a
 * timezone-naive ISO string ("YYYY-MM-DDTHH:mm:ss", no offset).
 *
 * Feed the result to a FullCalendar instance running in `timeZone="UTC"`: the
 * event then lands on the grid at its zone-local wall clock in every view
 * (month/week/day/list) without needing a FullCalendar timezone plugin. This
 * keeps grid placement consistent with event titles and the backend's
 * teacher-timezone day windows, so a late-evening lecture is not double-counted
 * in month view because the browser timezone pushed it past midnight.
 */
export function toZonedNaiveIsoString(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "00";
  // Intl can emit "24" for midnight in some environments; normalize to "00".
  const hour = get("hour") === "24" ? "00" : get("hour");

  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}:${get("second")}`;
}

const RELATIVE_TIME_UNITS = [
  { key: "year", minutes: 365 * 24 * 60 },
  { key: "month", minutes: 30 * 24 * 60 },
  { key: "day", minutes: 24 * 60 },
  { key: "hour", minutes: 60 },
  { key: "minute", minutes: 1 },
] as const;

type RelativeTimeUnit = (typeof RELATIVE_TIME_UNITS)[number]["key"];

export function formatLectureRelativeTime(date: Date, locale: string): string {
  const diffMinutes = Math.round((date.getTime() - Date.now()) / 60000);
  const isFuture = diffMinutes >= 0;
  let remainingMinutes = Math.abs(diffMinutes);

  if (remainingMinutes < 1) {
    return locale === "ar" ? "الآن" : "now";
  }

  const numberFormatter = new Intl.NumberFormat(resolveDateTimeLocale(locale));
  const parts: string[] = [];

  for (const unit of RELATIVE_TIME_UNITS) {
    if (parts.length === 2) {
      break;
    }

    const value = Math.floor(remainingMinutes / unit.minutes);

    if (value <= 0) {
      continue;
    }

    parts.push(formatRelativeTimePart(value, unit.key, locale, numberFormatter));
    remainingMinutes -= value * unit.minutes;
  }

  const value = parts.join(locale === "ar" ? " و" : " ");

  if (locale === "ar") {
    return isFuture ? `بعد ${value}` : `منذ ${value}`;
  }

  return isFuture ? `in ${value}` : `${value} ago`;
}

export function formatLectureDurationUntilStart(milliseconds: number, locale: string): string {
  return formatLectureRelativeTime(new Date(Date.now() + milliseconds), locale);
}

function formatRelativeTimePart(
  value: number,
  unit: RelativeTimeUnit,
  locale: string,
  numberFormatter: Intl.NumberFormat
): string {
  const labels: Record<RelativeTimeUnit, { en: [string, string]; ar: string }> = {
    year: { en: ["year", "years"], ar: "سنة" },
    month: { en: ["month", "months"], ar: "شهر" },
    day: { en: ["day", "days"], ar: "يوم" },
    hour: { en: ["hour", "hours"], ar: "ساعة" },
    minute: { en: ["minute", "minutes"], ar: "دقيقة" },
  };

  if (locale === "ar") {
    return `${numberFormatter.format(value)} ${labels[unit].ar}`;
  }

  const [singular, plural] = labels[unit].en;

  return `${numberFormatter.format(value)} ${value === 1 ? singular : plural}`;
}

export function formatCalendarDate(
  dateString: string,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateString) return dateString;
  const parts = dateString.split("-");
  if (parts.length !== 3) return dateString;
  const [year, month, day] = parts.map(Number);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return dateString;
  }
  const localDate = new Date(year, month - 1, day);
  try {
    return new Intl.DateTimeFormat(resolveDateTimeLocale(locale), options).format(localDate);
  } catch {
    return dateString;
  }
}

export function getLocalizedDayBoundaries(now: Date, timeZone: string) {
  const localDateStr = now.toLocaleString("en-US", { timeZone });
  const localNow = new Date(localDateStr);
  
  const todayStart = new Date(localNow);
  todayStart.setHours(0, 0, 0, 0);

  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);

  const thisWeekStart = new Date(todayStart);
  thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());

  return { todayStart, tomorrowStart, thisWeekStart };
}
